/**
 *
 * IBM Confidential
 *
 * (C) Copyright IBM Corp. 2022, 2024
 *
 * The source code for this program is not published or otherwise
 * divested of its trade secrets, irrespective of what has been
 * deposited with the U. S. Copyright Office
 *
 * US Government Users Restricted Rights - Use, duplication or
 * disclosure restricted by GSA ADP Schedule Contract with IBM Corp.
 *
 **/
import { Loading } from 'carbon-components-react';
import _ from 'lodash';
import React, { useEffect, useState } from 'react';
import { useFormContext } from 'react-hook-form';
import { FormattedMessage } from 'react-intl';

import { useAppSelector } from '@ibm-watson/shared/store';
import { useFormHelpers } from '../../../hooks/useFormHelpers';
import { selectDraftIntegration } from '../../../store/channels/selectors';
import { FIELDS } from '../schema';
import { SelectEventsMessages as messages } from './messages';
import { SelectEventsContent } from './SelectEventsContent';

import './SelectEventsTab.scss';

interface SelectEventsProps {
  isLive: boolean;
  readOnly: boolean;
}
export const SelectEvents = ({ isLive, readOnly }: SelectEventsProps) => {
  const formMethods = useFormContext();
  const { getFieldValue, updateFieldValue } = useFormHelpers(formMethods);

  const enabledEvents = getFieldValue(FIELDS.ENABLED_EVENTS);

  const draftIntegration = useAppSelector(selectDraftIntegration);

  const [initialSetup, setInitialSetup] = useState<boolean>(true);

  //by default, message handled is selected in the intial setup
  useEffect(() => {
    if (_.isEmpty(enabledEvents) && initialSetup) {
      const selected = [{ name: 'message_handled' }];
      updateFieldValue(FIELDS.ENABLED_EVENTS, selected);
    }
  }, [enabledEvents, initialSetup, updateFieldValue]);

  const selectionUpdated = values => {
    const events = [];
    _.forEach(values, item => events.push({ name: item }));
    // sets the field to be dirty since there is an upate
    updateFieldValue(FIELDS.ENABLED_EVENTS, draftIntegration.integration_id);
    //updates the actual value for the field
    updateFieldValue(FIELDS.ENABLED_EVENTS, events);
    //set to false since selection is different from initial
    setInitialSetup(false);
  };

  return (
    <div className="SelectEvents">
      <div className="SelectEvents__header">
        <div className="SelectEvents__title">
          <FormattedMessage {...messages.selectEvents} />
        </div>
        <div className="SelectEvents__description">
          <FormattedMessage
            {...messages.selectEventsDescription}
            values={{ env: isLive ? 'Live' : 'Draft' }}
          />
        </div>
      </div>
      <div className="TabContent__spacer32" />
      {_.isEmpty(enabledEvents) && initialSetup ? (
        <Loading />
      ) : (
        <SelectEventsContent
          readOnly={readOnly}
          selectionUpdated={selectionUpdated}
        />
      )}
    </div>
  );
};
